import type {CreateExtArgs, CreateExtensionArgument, Extension, ExtensionAPI} from '@wix/document-manager-core'
import _ from 'lodash'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import type {
    CompRef,
    CompStructure,
    ControllerConnectionItem,
    ConnectionItem,
    PossibleViewModes,
    Pointer,
    SerializedCompStructure,
    WixCodeConnectionItem
} from '@wix/document-services-types'
import {DATA_TYPES, MASTER_PAGE_ID, VIEW_MODES} from '../constants/constants'
import {getWixCodeConnectionItem} from '../utils/nicknamesUtils'
import type {NicknameContextExtensionAPI} from './nicknameContext'
import type {NicknamesExtensionAPI} from './nicknames'
import type {ConnectionsAPI} from './connections'
import type {PageExtensionAPI} from './page'
import type {ComponentsMetadataAPI} from './componentsMetadata/componentsMetadata'

export interface ComponentCodeAPI {
    updateConnectionItemsNickname(
        connectionItems: ConnectionItem[],
        compPointer: CompRef,
        pagePointer: CompRef,
        contextInCurrentContainer: CompRef
    ): void
    updateConnectionsIfNeeded(
        compPointer: CompRef,
        containerPointer: CompRef,
        compDefinition: SerializedCompStructure
    ): void
    updateNicknameContextByNewContainer(
        compPointer: CompRef,
        componentDefinition: SerializedCompStructure,
        newContainerPointer: CompRef
    ): void
    generateNicknamesForPages(
        requestedPages: string[],
        viewModeIfSupported?: PossibleViewModes,
        shouldCommitPerPage?: boolean
    ): void
}

export type ComponentCodeExtensionAPI = ExtensionAPI & {
    componentCode: ComponentCodeAPI
}
type NicknamesMap = Record<string, string>
const ORIGINAL_CONTEXT_FIELD = 'originalNicknameContext'

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): ComponentCodeExtensionAPI => {
        const findSerializedConnectionByContext = (
            context: CompRef | null,
            compConnections: ConnectionItem[]
        ): ConnectionItem => {
            const {extensionAPI} = createExtArgs
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            if (context) {
                const {id: controllerId} = dataModel.components.getItem(context, DATA_TYPES.data) || {}
                if (controllerId) {
                    return _.find(compConnections, {controllerId}) as ConnectionItem
                }
            }
            return getWixCodeConnectionItem(compConnections) as ConnectionItem
        }

        const getContextControllerId = (context: CompRef | null) => {
            const {extensionAPI} = createExtArgs
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            if (!context) {
                return
            }
            const dataItem = dataModel.components.getItem(context, DATA_TYPES.data)
            return _.get(dataItem, ['id'])
        }

        const removeConnectionFromSerializedComponentIfInvalidNickname = (
            compPointer: CompRef,
            context: null | CompRef,
            connectionItems: ConnectionItem[],
            pagePointer: CompRef
        ) => {
            const nicknameConnectionItem = findSerializedConnectionByContext(context, connectionItems)
            const {extensionAPI} = createExtArgs
            const {nicknames} = extensionAPI as NicknamesExtensionAPI
            if (
                nicknameConnectionItem &&
                nicknames.hasComponentWithThatNickname(
                    pagePointer,
                    (nicknameConnectionItem as ControllerConnectionItem).role,
                    compPointer
                )
            ) {
                _.remove(connectionItems, nicknameConnectionItem)
            }
        }

        const updateConnectionItemsNickname = (
            connectionItems: ConnectionItem[],
            compPointer: CompRef,
            pagePointer: CompRef,
            contextInCurrentContainer: CompRef
        ) => {
            const {extensionAPI} = createExtArgs
            const {nicknameContext} = extensionAPI as NicknameContextExtensionAPI
            const connectionItem = findSerializedConnectionByContext(contextInCurrentContainer, connectionItems)
            if (!connectionItem) {
                return
            }
            const context = nicknameContext.getNicknameContext(pagePointer)
            const contextControllerId = getContextControllerId(context)
            if (contextControllerId) {
                ;(connectionItem as ControllerConnectionItem).controllerId = contextControllerId
            }
            removeConnectionFromSerializedComponentIfInvalidNickname(compPointer, context, connectionItems, pagePointer)
        }

        const fixWixCodeConnections = (context: null | CompRef, compDefinition: CompStructure) => {
            const items = _.get(compDefinition, ['connections', 'items'], [])
            const wixCodeConnectionItem: WixCodeConnectionItem = _.find(items, {type: 'WixCodeConnectionItem'})
            if (context && wixCodeConnectionItem) {
                const {role} = wixCodeConnectionItem
                const {extensionAPI} = createExtArgs
                const {dataModel} = extensionAPI as DataModelExtensionAPI
                const {connections} = extensionAPI as ConnectionsAPI

                const controllerId = dataModel.components.getItem(context, DATA_TYPES.data).id
                const connectionItem = connections.createConnectionItem(role, controllerId)

                compDefinition.connections.items = _(items)
                    .without(wixCodeConnectionItem)
                    .concat([connectionItem])
                    .value()
            }
        }
        const fixOrRemoveConnections = (
            compPointer: CompRef,
            compDefinition: CompStructure,
            nicknameContext: CompRef | null,
            pagePointer: CompRef
        ) => {
            fixWixCodeConnections(nicknameContext, compDefinition)
            removeConnectionFromSerializedComponentIfInvalidNickname(
                compPointer,
                nicknameContext,
                _.get(compDefinition, ['connections', 'items']),
                pagePointer
            )
        }
        const updateConnectionsIfNeeded = (
            compPointer: CompRef,
            containerPointer: CompRef,
            compDefinition: SerializedCompStructure
        ) => {
            const {pointers, extensionAPI} = createExtArgs
            const {nicknameContext} = extensionAPI as NicknameContextExtensionAPI

            const pagePointer = pointers.structure.getPageOfComponent(containerPointer)
            const nicknameCtxVal = nicknameContext.getNicknameContext(pagePointer as CompRef)

            fixOrRemoveConnections(compPointer, compDefinition, nicknameCtxVal, pagePointer as CompRef)
        }
        const updateNicknameContextByNewContainer = (
            compPointer: CompRef,
            componentDefinition: SerializedCompStructure,
            newContainerPointer: CompRef
        ) => {
            const {pointers} = createExtArgs
            const connectionItems = _.get(componentDefinition, ['connections', 'items'])
            if (_.isEmpty(connectionItems)) {
                return
            }

            const contextInCurrentContainer = _.get(componentDefinition, ['custom', ORIGINAL_CONTEXT_FIELD])
            if (contextInCurrentContainer) {
                const pagePointer = pointers.structure.getPageOfComponent(newContainerPointer)
                updateConnectionItemsNickname(
                    connectionItems,
                    compPointer,
                    pagePointer as CompRef,
                    contextInCurrentContainer
                )
            }
        }

        const copyNicknamesFromDesktopToViewMode = (pageId: string) => {
            const {pointers, extensionAPI} = createExtArgs
            const {nicknameContext} = extensionAPI as NicknameContextExtensionAPI
            const {nicknames} = extensionAPI as NicknamesExtensionAPI
            const mobilePage = pointers.structure.getPage(pageId, VIEW_MODES.MOBILE) as CompRef
            const context = nicknameContext.getNicknameContext(mobilePage)
            const mobileComponents = nicknames.getComponentsInContainer(mobilePage)
            const desktopPage = pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP) as CompRef
            _(mobileComponents)
                .filter(compPointer => nicknames.shouldSetNickname(compPointer as CompRef, context))
                .forEach((viewModeCompPointer: Pointer) => {
                    const desktopCompPointer = pointers.structure.getComponent(
                        viewModeCompPointer.id,
                        desktopPage
                    ) as CompRef
                    if (desktopCompPointer) {
                        const desktopCompNickname = nicknames.getNickname(desktopCompPointer, context)
                        if (desktopCompNickname) {
                            nicknames.setNickname(viewModeCompPointer as CompRef, desktopCompNickname, context)
                        }
                    }
                })
        }

        const generateNicknamesForComponents = (
            compsPointers: CompRef[],
            usedNickNames: NicknamesMap,
            context: CompRef | null,
            pagePointer: CompRef
        ): CompRef[] => {
            const {extensionAPI} = createExtArgs
            const {componentsMetadata} = extensionAPI as ComponentsMetadataAPI
            const {nicknames} = extensionAPI as NicknamesExtensionAPI

            const compGroupsByBaseNickname = _.groupBy(compsPointers, compPointer =>
                componentsMetadata.getDefaultNickname(compPointer)
            )

            return _.flatMap(compGroupsByBaseNickname, function (comps, defaultCompNickname) {
                return nicknames.setNicknamesForComponentsWithoutNickname(
                    comps,
                    defaultCompNickname,
                    usedNickNames,
                    context,
                    pagePointer
                )
            })
        }
        const generateNicknamesForPage = (usedNicknames: NicknamesMap, pagePointer: CompRef): any => {
            const {extensionAPI} = createExtArgs
            const {nicknames} = extensionAPI as NicknamesExtensionAPI
            const context = (extensionAPI as NicknameContextExtensionAPI).nicknameContext.getNicknameContext(
                pagePointer
            )
            const allCompsInPageContext = nicknames.getComponentsInContainer(pagePointer, context) as CompRef[]
            const usedNicknamesInPage = nicknames.getNicknames(allCompsInPageContext as CompRef[], context)
            const allUsedNickNames = _.assign({}, usedNicknamesInPage, usedNicknames)

            const componentsWithNewNicknamesInPage = generateNicknamesForComponents(
                allCompsInPageContext,
                allUsedNickNames,
                context,
                pagePointer
            )

            return _.assign(usedNicknamesInPage, nicknames.getNicknames(componentsWithNewNicknamesInPage, context))
        }

        const generateNicknamesForPageInViewMode = (
            usedNicknames: NicknamesMap,
            pageId: string,
            viewMode: string
        ): NicknamesMap => {
            const {pointers} = createExtArgs
            const desktopPage = pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP) as CompRef
            const pageNicknames = generateNicknamesForPage(usedNicknames, desktopPage)
            if (viewMode === VIEW_MODES.MOBILE) {
                copyNicknamesFromDesktopToViewMode(pageId)
                const mobilePage = pointers.structure.getPage(pageId, VIEW_MODES.MOBILE) as CompRef
                _.assign(pageNicknames, generateNicknamesForPage(usedNicknames, mobilePage))
            }

            return pageNicknames
        }

        const getUsedNicknamesFromPageInViewMode = (pageId: string, viewMode: string): NicknamesMap => {
            const {pointers, extensionAPI} = createExtArgs
            const {nicknames} = extensionAPI as NicknamesExtensionAPI

            const desktopPage = pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP) as CompRef
            const nickNames = nicknames.getNicknames(
                pointers.structure.getChildrenRecursivelyRightLeftRootIncludingRoot(desktopPage) as CompRef[],
                null
            )
            if (viewMode === VIEW_MODES.MOBILE) {
                const mobilePage = pointers.structure.getPage(pageId, VIEW_MODES.MOBILE) as CompRef
                _.assign(
                    nickNames,
                    nicknames.getNicknames(
                        pointers.structure.getChildrenRecursivelyRightLeftRootIncludingRoot(mobilePage) as CompRef[],
                        null
                    )
                )
            }
            return nickNames
        }
        const generateNicknamesForPages = (
            requestedPages: string[],
            viewModeIfSupported?: PossibleViewModes,
            shouldCommitPerPage: boolean = false
        ) => {
            const {extensionAPI, dal} = createExtArgs
            const viewMode = viewModeIfSupported || VIEW_MODES.DESKTOP
            const masterPageNicknames = getUsedNicknamesFromPageInViewMode(MASTER_PAGE_ID, viewMode)
            if (!requestedPages.includes(MASTER_PAGE_ID)) {
                for (const pageId of requestedPages) {
                    generateNicknamesForPageInViewMode(masterPageNicknames, pageId, viewMode)
                    if (shouldCommitPerPage) {
                        dal.commitTransaction('componentCode.generateNicknamesForSite')
                    }
                }
                return
            }

            const generatedNicknames: NicknamesMap = {}
            for (const pageId of _.without(requestedPages, 'masterPage')) {
                _.assign(generatedNicknames, generateNicknamesForPageInViewMode(masterPageNicknames, pageId, viewMode))
                if (shouldCommitPerPage) {
                    dal.commitTransaction('componentCode.generateNicknamesForSite')
                }
            }
            const allPagesIds = (extensionAPI as PageExtensionAPI).page.getAllPagesIds(false)

            const otherPages = _.difference(allPagesIds, requestedPages)
            const otherNicknames: NicknamesMap = {}
            for (const pageId of otherPages) {
                _.assign(otherNicknames, getUsedNicknamesFromPageInViewMode(pageId, viewMode))
            }

            const nicknamesInAllSite = _.assign({}, masterPageNicknames, generatedNicknames, otherNicknames)
            generateNicknamesForPageInViewMode(nicknamesInAllSite, MASTER_PAGE_ID, viewMode)
        }
        return {
            componentCode: {
                generateNicknamesForPages,
                updateConnectionItemsNickname,
                updateConnectionsIfNeeded,
                updateNicknameContextByNewContainer
            }
        }
    }
    return {
        name: 'componentCode',
        dependencies: new Set(['dataModel', 'components']),
        createExtensionAPI
    }
}

export {createExtension}
